import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { useI18n } from '@/i18n';
import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-ink/20 backdrop-blur-[2px]', className)}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

const dialogWidths = {
  md: 'max-w-[520px]',
  lg: 'max-w-[720px]',
} as const;

export interface DialogContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  size?: keyof typeof dialogWidths;
  showClose?: boolean;
}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, title, description, size = 'md', showClose = true, children, ...props }, ref) => {
  const { t } = useI18n();

  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        // Radix points aria-describedby at a Description that may not exist and warns
        // about it; clearing the attribute when there is no description silences that.
        {...(description ? {} : { 'aria-describedby': undefined })}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2',
          'animate-slide-up-fade rounded-lg border border-line bg-surface shadow-pop',
          dialogWidths[size],
          className,
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-4 px-4 pb-3 pt-4">
          <div className="min-w-0">
            <DialogPrimitive.Title className="text-md font-medium text-ink">{title}</DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-1 text-sm text-ink-muted">
                {description}
              </DialogPrimitive.Description>
            ) : null}
          </div>
          {showClose ? (
            <DialogPrimitive.Close
              aria-label={t('action.close')}
              className="-mr-1 -mt-1 inline-flex size-[26px] shrink-0 items-center justify-center rounded text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
            >
              <X className="size-3.5" aria-hidden="true" />
            </DialogPrimitive.Close>
          ) : null}
        </div>
        <div className="px-4 pb-4 text-base">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
  },
);
DialogContent.displayName = 'DialogContent';

const DialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('-mx-4 -mb-4 mt-4 flex items-center justify-end gap-2 border-t border-line px-4 py-3', className)}
      {...props}
    />
  ),
);
DialogFooter.displayName = 'DialogFooter';

export { Dialog, DialogTrigger, DialogClose, DialogOverlay, DialogContent, DialogFooter };
